import { Handle, Position } from "@xyflow/react";
import type { NodeProps } from "@xyflow/react";
import { ChartPie } from "lucide-react";
import type { SystemFlowNode } from "../graph/types";
import { useChainStore } from "../store/chainStore";
import { useUiStore } from "../store/uiStore";
import { HUB_LETTER } from "../utils/closestHub";
import { CLASS_LABEL } from "../lib/systemClass";
import { SystemIdentity } from "./SystemIdentity";
import { PresencePopover } from "./PresencePopover";

export function SystemNode({ data: system, selected }: NodeProps<SystemFlowNode>) {
  const rootSystemId = useChainStore((s) => s.rootSystemId);
  const highlightJSpaceLastThree = useUiStore(
    (s) => s.highlightJSpaceLastThree,
  );
  const isRoot = system.id === rootSystemId;
  const { isTradeHub, hubInfo, isCurrent } = system;
  const hasStatics = (system.statics?.length ?? 0) > 0;

  return (
    <div
      className={`system-node${
        system.hostility ? ` is-hostility-${system.hostility}` : ""
      }${isRoot ? " is-root" : ""}${isCurrent ? " is-current" : ""}${selected ? " is-selected" : ""}${isTradeHub ? " is-trade-hub" : ""}`}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ opacity: 0, pointerEvents: "none" }}
        isConnectable={false}
      />
      {system.presence.length > 0 && (
        <PresencePopover pilots={system.presence} />
      )}
      <div className="system-node__row">
        <SystemIdentity
          systemClass={system.class}
          name={system.name}
          effect={system.effect}
          plainName={!highlightJSpaceLastThree}
        >
          {hubInfo && (
            <>
              {" "}
              <span className="system-node__hub">
                ({hubInfo.closest.jumps}
                {HUB_LETTER[hubInfo.closest.hub]}
                {hubInfo.jita && <>/{hubInfo.jita.jumps}J</>})
              </span>
            </>
          )}
        </SystemIdentity>
      </div>
      {(system.isShattered || system.alias || hasStatics) && (
        <div className="system-node__footer">
          {system.isShattered && (
            <span
              className="system-node__shattered"
              title="Shattered system"
              aria-label="Shattered system"
            >
              <ChartPie size={12} aria-hidden="true" />
            </span>
          )}
          {system.alias && (
            <span className="system-node__alias">{system.alias}</span>
          )}
          {system.statics && system.statics.length > 0 && (
            <div className="system-node__statics">
              {system.statics.map((s) => (
                <span
                  key={s.code}
                  className={`system-node__static system-class--${s.class}`}
                >
                  {CLASS_LABEL[s.class]}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      <Handle
        type="source"
        position={Position.Top}
        style={{ opacity: 0, pointerEvents: "none" }}
        isConnectable={false}
      />
    </div>
  );
}
